import { StorageHandler } from '@/data/storage/StorageHandler';
import { create } from 'zustand';

/**
 * Store de Autenticação com Zustand
 *
 * Este store gerencia o estado global de autenticação, incluindo:
 * - Informações do usuário autenticado (ID, nome, email, etc.).
 * - Tokens de autenticação (id_token, access_token, refresh_token).
 * - Funções para login, logout e manipulação de tokens.
 */

interface UserToken {
  id_token: string;
  access_token: string;
  refresh_token: string;
  expiresAt: string;
  expiresIn: number;
}

/**
 * todo: Mover para um arquivo separado
 * Interface do modelo de usuário
 */
interface UserModel {
  id: string;
  name: string;
  email: string;
  surname?: string;
  picture?: string;
  birthdate?: string;
}

interface AuthState {
  user: UserModel | null;

  tokens: UserToken | null;

  login: (user: UserModel, tokens: UserToken) => void;

  logout: () => void;

  getToken: () => UserToken | null;
  getUser: () => UserModel | null;
  updateTokens: (tokens: UserToken) => void;
  clearTokens: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  tokens: null,

  login: (user, tokens) => {
    set({ user, tokens });
    StorageHandler.setStorage(StorageHandler.KEY_AUTH, JSON.stringify(tokens));
    StorageHandler.setStorage(StorageHandler.KEY_USER, JSON.stringify(user));
  },

  logout: () => {
    set({ user: null, tokens: null });
    StorageHandler.removeStorage(StorageHandler.KEY_AUTH);
    StorageHandler.removeStorage(StorageHandler.KEY_USER);
  },

  /**
   * load the tokens from the store or from the storage to memory
   * @returns Retorna os tokens de autenticação, se existirem
   */
  getToken: () => {
    const { tokens } = get();

    if (tokens) {
      return tokens;
    }

    const storedTokens = StorageHandler.getStorage(StorageHandler.KEY_AUTH);
    if (storedTokens) {
      const parsedTokens = JSON.parse(storedTokens);
      set({ tokens: parsedTokens }); // Atualiza o estado com os tokens armazenados
      return parsedTokens;
    }

    return null;
  },

  /**
   * Load the user from the store or from the storage to memory
   * @returns Retorna o usuário autenticado, se existir
   */
  getUser: () => {
    const { user } = get();

    if (user) {
      return user;
    }

    const storedUser = StorageHandler.getStorage(StorageHandler.KEY_USER);
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      set({ user: parsedUser }); // Atualiza o estado com o usuário armazenado
      return parsedUser;
    }

    return null;
  },

  updateTokens: (newTokens) => {
    const { user } = get();

    set({ tokens: newTokens });
    StorageHandler.setStorage(
      StorageHandler.KEY_AUTH,
      JSON.stringify(newTokens)
    );

    // Mantém o usuário no estado se estiver logado
    if (user) {
      set({ user });
    }
  },

  clearTokens: () => {
    set({ tokens: null });
    StorageHandler.removeStorage(StorageHandler.KEY_AUTH);
  },
}));
